import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, BookHeart, Mail, Lock, User as UserIcon, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useCustomer } from "@/context/CustomerContext";
import { api } from "@/lib/api";

export default function AuthDialog() {
  const { authOpen, setAuthOpen, login, register } = useCustomer();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (authOpen) {
      setMode("login");
      setForm({ name: "", email: "", password: "" });
      setSent(false);
    }
  }, [authOpen]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", {
          email: form.email.trim(),
          origin: window.location.origin,
        });
        setSent(true);
      } else if (mode === "login") {
        const res = await login(form.email.trim(), form.password);
        if (res?.is_admin) {
          setAuthOpen(false);
          toast.success("Bienvenido, administrador");
          window.location.assign("/admin");
          return;
        }
        toast.success("¡Bienvenido de nuevo!");
        setAuthOpen(false);
        setForm({ name: "", email: "", password: "" });
      } else {
        await register(form.name.trim(), form.email.trim(), form.password);
        toast.success("¡Cuenta creada con éxito!");
        setAuthOpen(false);
        setForm({ name: "", email: "", password: "" });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ocurrió un error, intenta de nuevo");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full rounded-xl border-2 border-[var(--lc-border)] focus:border-[var(--lc-primary)] focus:ring-4 focus:ring-[var(--lc-primary)]/15 outline-none transition-colors pl-11 pr-4 py-3 bg-[var(--lc-bg)] text-[var(--lc-ink)]";

  const title = mode === "login" ? "Iniciar sesión" : mode === "register" ? "Crear cuenta" : "Recuperar contraseña";

  return (
    <Dialog open={authOpen} onOpenChange={setAuthOpen}>
      <DialogContent className="max-w-md bg-white" data-testid="customer-auth-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-extrabold text-2xl text-[var(--lc-ink)] flex items-center gap-2">
            <BookHeart className="w-6 h-6 text-[var(--lc-primary)]" />
            {title}
          </DialogTitle>
          <DialogDescription className="text-[var(--lc-slate)]">
            {mode === "login"
              ? "Accede a tu cuenta para guardar tus libros favoritos."
              : mode === "register"
              ? "Crea tu cuenta para guardar tus libros favoritos y comprar más rápido."
              : "Te enviaremos un enlace a tu correo para crear una nueva contraseña."}
          </DialogDescription>
        </DialogHeader>

        {mode === "forgot" && sent ? (
          <div className="text-center py-4" data-testid="forgot-sent">
            <CheckCircle2 className="w-14 h-14 text-[var(--lc-success)] mx-auto mb-3" />
            <p className="text-[var(--lc-ink)] font-semibold">Revisa tu correo</p>
            <p className="text-[var(--lc-slate)] text-sm mt-1">
              Si existe una cuenta con <strong>{form.email.trim()}</strong>, te enviamos un enlace para
              restablecer tu contraseña. Vence en 1 hora.
            </p>
            <button
              onClick={() => { setMode("login"); setSent(false); }}
              data-testid="forgot-back-login"
              className="mt-5 font-bold text-[var(--lc-primary)] hover:underline"
            >
              Volver a iniciar sesión
            </button>
          </div>
        ) : (
          <>
            <form onSubmit={submit} className="space-y-3 mt-1">
              {mode === "register" && (
                <div className="relative">
                  <UserIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--lc-slate)]" />
                  <input className={field} data-testid="auth-name" placeholder="Tu nombre" required
                    value={form.name} onChange={(e) => update("name", e.target.value)} />
                </div>
              )}
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--lc-slate)]" />
                <input className={field} type="email" data-testid="auth-email" placeholder="correo@ejemplo.com" required
                  value={form.email} onChange={(e) => update("email", e.target.value)} />
              </div>
              {mode !== "forgot" && (
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--lc-slate)]" />
                  <input className={field} type="password" data-testid="auth-password" placeholder="Contraseña (mín. 6)" required minLength={6}
                    value={form.password} onChange={(e) => update("password", e.target.value)} />
                </div>
              )}

              {mode === "login" && (
                <div className="text-right -mt-1">
                  <button type="button" onClick={() => setMode("forgot")} data-testid="forgot-password-link"
                    className="text-sm font-semibold text-[var(--lc-primary)] hover:underline">
                    ¿Olvidaste tu contraseña?
                  </button>
                </div>
              )}

              <button type="submit" disabled={loading} data-testid="auth-submit"
                className="w-full flex items-center justify-center gap-2 rounded-full bg-[var(--lc-primary)] hover:bg-[var(--lc-primary-hover)] text-white py-3.5 font-display font-extrabold text-lg transition-colors disabled:opacity-60">
                {loading && <Loader2 className="w-5 h-5 animate-spin" />}
                {mode === "login" ? "Entrar" : mode === "register" ? "Crear cuenta" : "Enviar enlace"}
              </button>
            </form>

            <p className="text-center text-sm text-[var(--lc-slate)]">
              {mode === "forgot" ? (
                <button onClick={() => setMode("login")} data-testid="auth-toggle-mode"
                  className="font-bold text-[var(--lc-primary)] hover:underline">
                  Volver a iniciar sesión
                </button>
              ) : (
                <>
                  {mode === "login" ? "¿No tienes cuenta?" : "¿Ya tienes cuenta?"}{" "}
                  <button
                    onClick={() => setMode(mode === "login" ? "register" : "login")}
                    data-testid="auth-toggle-mode"
                    className="font-bold text-[var(--lc-primary)] hover:underline"
                  >
                    {mode === "login" ? "Crear cuenta" : "Iniciar sesión"}
                  </button>
                </>
              )}
            </p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
